import React, { useRef, useState } from 'react';
import { parseISODate } from '../utils/formDate';
import { periodWindow } from '../utils/dateRange';
import { holdScrollPosition } from '../utils/holdScrollPosition';
import './PeriodPager.css';

// Un glissement compte s'il est franc et surtout horizontal : en deçà, c'est un défilement
// vertical de la page qui a un peu dévié, et il ne doit pas changer de période.
const SWIPE_MIN_PX = 50;
const SWIPE_RATIO = 1.5;
const SWIPE_MAX_MS = 800;

/**
 * Gestes de glissement horizontal sur un élément, sans bloquer le défilement vertical :
 * on ne fait que lire le geste à la fin (pas de `preventDefault`).
 * Doigt vers la droite → `onSwipeRight`, vers la gauche → `onSwipeLeft`.
 */
export function useHorizontalSwipe({ onSwipeRight, onSwipeLeft }) {
  const start = useRef(null);
  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      start.current = e.touches.length === 1 ? { x: t.clientX, y: t.clientY, at: Date.now() } : null;
    },
    onTouchEnd: (e) => {
      const s = start.current;
      start.current = null;
      const t = e.changedTouches[0];
      if (!s || !t || Date.now() - s.at > SWIPE_MAX_MS) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
      if (dx > 0) onSwipeRight?.();
      else onSwipeLeft?.();
    },
    onTouchCancel: () => { start.current = null; },
  };
}

/**
 * Décalage de période d'un widget (0 = la période qui finit aujourd'hui). Revient à 0
 * quand la durée change : « 7 jours » choisi en étant remonté de trois mois ramène au
 * présent plutôt qu'à une semaine arbitraire du passé.
 */
export function usePeriodOffset(days) {
  const [state, setState] = useState({ days, offset: 0 });
  const offset = state.days === days ? state.offset : 0;
  const setOffset = (next) => setState({ days, offset: Math.max(0, next) });
  return [offset, setOffset];
}

const SHORT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
const SHORT_YEAR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

/** « 30 derniers jours », ou « 2 sept. – 1 oct. » une fois remonté (année si autre). */
export function periodLabel(days, offset) {
  if (offset === 0) return `${days} derniers jours`;
  const { start, end } = periodWindow(days, offset);
  const thisYear = new Date().getFullYear();
  const fmt = (d) => (d.getFullYear() === thisYear ? SHORT : SHORT_YEAR).format(d);
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Navigation dans le temps d'un widget à période : « ‹ période › » au-dessus du contenu,
 * et glissement au doigt sur le contenu — vers la droite pour remonter dans le passé,
 * vers la gauche pour revenir, jamais au-delà d'aujourd'hui.
 *
 * `earliest` (date du plus ancien relevé) borne le passé : inutile de remonter vers des
 * périodes où rien n'a été saisi. Le contenu glisse dans le sens du déplacement.
 */
const PeriodPager = ({ days, offset, onOffsetChange, earliest, children }) => {
  const navRef = useRef(null);
  const [direction, setDirection] = useState(null);

  const first = parseISODate(earliest);
  const canOlder = !!first && periodWindow(days, offset).start > first;
  const canNewer = offset > 0;

  const go = (next, dir) => {
    // La hauteur du contenu change d'une période à l'autre : la barre reste sous le doigt.
    holdScrollPosition(navRef.current);
    setDirection(dir);
    onOffsetChange(next);
  };
  const older = () => { if (canOlder) go(offset + 1, 'older'); };
  const newer = () => { if (canNewer) go(offset - 1, 'newer'); };
  const swipe = useHorizontalSwipe({ onSwipeRight: older, onSwipeLeft: newer });

  return (
    <>
      <div ref={navRef} className="period-pager__nav">
        <button
          type="button"
          className="period-pager__arrow"
          onClick={older}
          disabled={!canOlder}
          aria-label="Période précédente"
        >&#8249;</button>
        <span className="period-pager__label" aria-live="polite">{periodLabel(days, offset)}</span>
        <button
          type="button"
          className="period-pager__arrow"
          onClick={newer}
          disabled={!canNewer}
          aria-label="Période suivante"
        >&#8250;</button>
      </div>
      <div className="period-pager__area" {...swipe}>
        <div
          key={`${days}-${offset}`}
          className={`period-pager__page${direction ? ` period-pager__page--from-${direction}` : ''}`}
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default PeriodPager;
